import type { ProjectType } from "@reno-estimator/core";
import type { LeadQualification, LeadQualificationCategory, QualificationContext } from "./types.js";

/**
 * A modifier can bump a value-based tier up one step (e.g. structural work
 * plus a likely permit signals a more involved project than the dollar
 * figure alone suggests). Evaluated in order; the first bump per lead wins
 * per rule, but multiple distinct rules can each contribute one bump.
 */
export interface QualificationTierBumpRule {
  id: string;
  /** Developer-facing explanation of why this rule exists - never shown to the homeowner. */
  description: string;
  appliesTo(ctx: QualificationContext): boolean;
}

/**
 * A location rule can force a specific category regardless of the
 * value-based tier - e.g. flagging out-of-service-area postal codes as
 * lower priority, or a known high-value neighbourhood as an opportunity.
 * Empty by default (`DEFAULT_QUALIFICATION_CONFIG.locationOverrideRules`)
 * since a specific service area/geography is a business decision, not
 * something to guess at - see docs/lead-capture.md.
 */
export interface QualificationLocationOverrideRule {
  id: string;
  description: string;
  appliesTo(ctx: QualificationContext): boolean;
  category: LeadQualificationCategory;
}

export interface QualificationThresholds {
  /** Typical estimate at/above this is at least "mid_size_renovation". */
  midSizeMinTypical: number;
  /** Typical estimate at/above this is at least "major_renovation". */
  majorMinTypical: number;
  /** Typical estimate at/above this is "high_value_opportunity" outright. */
  highValueMinTypical: number;
}

export interface QualificationConfig {
  thresholds: QualificationThresholds;
  /** Project types that are always classified as "whole_home_renovation" regardless of dollar value. */
  wholeHomeProjectTypes: ProjectType[];
  tierBumpRules: QualificationTierBumpRule[];
  locationOverrideRules: QualificationLocationOverrideRule[];
}

const VALUE_TIER_ORDER: LeadQualificationCategory[] = [
  "small_project",
  "mid_size_renovation",
  "major_renovation",
  "high_value_opportunity"
];

export const DEFAULT_QUALIFICATION_THRESHOLDS: QualificationThresholds = {
  midSizeMinTypical: 15_000,
  majorMinTypical: 50_000,
  highValueMinTypical: 150_000
};

const structuralAndPermitCombo: QualificationTierBumpRule = {
  id: "structural_and_permit_combo",
  description: "Structural work plus a likely permit together signal a more involved, higher-complexity project than the dollar estimate alone suggests.",
  appliesTo: (ctx) => ctx.input.workType.structuralChanges === "yes" && ctx.input.designAndPermits.permitLikelyRequired === "yes"
};

const urgentTimeline: QualificationTierBumpRule = {
  id: "urgent_timeline",
  description: "A homeowner who wants to start ASAP on a project of meaningful size is a higher-intent lead worth prioritizing.",
  appliesTo: (ctx) => ctx.input.timeline.desiredStartWindow === "asap"
};

const luxuryFinish: QualificationTierBumpRule = {
  id: "luxury_finish_level",
  description: "A luxury finish-level selection tends to correlate with a larger realized project value than the initial estimate alone, since finish selections are often refined upward during the actual quote.",
  appliesTo: (ctx) => ctx.input.finishLevel === "luxury"
};

export const DEFAULT_QUALIFICATION_CONFIG: QualificationConfig = {
  thresholds: DEFAULT_QUALIFICATION_THRESHOLDS,
  wholeHomeProjectTypes: ["whole_home_renovation"],
  tierBumpRules: [structuralAndPermitCombo, urgentTimeline, luxuryFinish],
  locationOverrideRules: []
};

function baseValueTier(typicalAmount: number, thresholds: QualificationThresholds): LeadQualificationCategory {
  if (typicalAmount >= thresholds.highValueMinTypical) return "high_value_opportunity";
  if (typicalAmount >= thresholds.majorMinTypical) return "major_renovation";
  if (typicalAmount >= thresholds.midSizeMinTypical) return "mid_size_renovation";
  return "small_project";
}

function bumpTier(category: LeadQualificationCategory, steps: number): LeadQualificationCategory {
  const index = VALUE_TIER_ORDER.indexOf(category);
  if (index < 0) return category; // whole_home_renovation isn't on the value ladder - never bumped
  const bumped = Math.min(index + steps, VALUE_TIER_ORDER.length - 1);
  return VALUE_TIER_ORDER[bumped]!;
}

/**
 * Classifies a lead into one of the suggested qualification categories,
 * using the estimate value, project type, location, timeline, scope,
 * finish level, structural work, and permit likelihood - exactly the
 * inputs the Phase 16 brief calls out. Every rule is passed in via
 * `config` (defaulting to DEFAULT_QUALIFICATION_CONFIG) rather than
 * hard-coded, so an administrator can retune thresholds or add/remove
 * modifier rules without touching this function. The returned
 * `internalNotes`/`matchedRuleId` are for whoever administers leads -
 * the homeowner-facing UI never renders them (see docs/lead-capture.md).
 */
export function classifyLead(ctx: QualificationContext, config: QualificationConfig = DEFAULT_QUALIFICATION_CONFIG): LeadQualification {
  const internalNotes: string[] = [];

  for (const rule of config.locationOverrideRules) {
    if (rule.appliesTo(ctx)) {
      internalNotes.push(`Location override "${rule.id}" applied: ${rule.description}`);
      return { category: rule.category, matchedRuleId: rule.id, internalNotes };
    }
  }

  if (config.wholeHomeProjectTypes.includes(ctx.input.projectType)) {
    internalNotes.push(`Project type "${ctx.input.projectType}" is configured as a whole-home project type.`);
    return { category: "whole_home_renovation", matchedRuleId: "whole_home_project_type", internalNotes };
  }

  const typicalAmount = ctx.result.estimate.typical.amount;
  const base = baseValueTier(typicalAmount, config.thresholds);
  internalNotes.push(`Base tier from typical estimate ($${typicalAmount.toLocaleString("en-CA")}): "${base}".`);

  let bumps = 0;
  const matchedRuleIds: string[] = [];
  for (const rule of config.tierBumpRules) {
    if (rule.appliesTo(ctx)) {
      bumps += 1;
      matchedRuleIds.push(rule.id);
      internalNotes.push(`Tier bump from rule "${rule.id}": ${rule.description}`);
    }
  }

  const finalCategory = bumps > 0 ? bumpTier(base, bumps) : base;
  if (finalCategory !== base) {
    internalNotes.push(`Final tier after ${bumps} bump(s): "${finalCategory}".`);
  }

  return {
    category: finalCategory,
    matchedRuleId: matchedRuleIds.length > 0 ? matchedRuleIds.join("+") : "base_value_tier",
    internalNotes
  };
}
